import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";

interface Asset {
  id: number;
  filename: string;
  url: string;
  contentType: string;
  width?: number;
  height?: number;
  altText?: string;
  tags?: string;
  source: string;
  fileSizeBytes: number;
  createdAt: string;
}

interface Props {
  onSelect: (asset: Asset) => void;
  onClose: () => void;
}

export default function AssetPicker({ onSelect, onClose }: Props) {
  const api = useApi();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureDesc, setCaptureDesc] = useState("");
  const [captureUrl, setCaptureUrl] = useState("");
  const [showCapture, setShowCapture] = useState(false);
  const [search, setSearch] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await (await api.admin.get("api/kb/assets")).json();
      setAssets(data.items || []);
    } catch (err) {
      setError(`Failed to load assets: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    try {
      setUploading(true);
      const form = new FormData();
      form.append("file", file);
      const data = await (await api.admin.post("api/kb/assets/upload", { body: form })).json();
      await load();
      onSelect(data);
    } catch (err) {
      setError(`Upload failed: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const capture = async () => {
    if (!captureDesc.trim()) return;
    try {
      setCapturing(true);
      const data = await (await api.admin.post("api/kb/assets/capture", {
        body: JSON.stringify({ description: captureDesc, url: captureUrl || undefined }),
        headers: { "Content-Type": "application/json" }
      })).json();
      await load();
      setShowCapture(false);
      setCaptureDesc("");
      setCaptureUrl("");
      onSelect(data);
    } catch (err) {
      setError(`Capture failed: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setCapturing(false);
    }
  };

  const filtered = assets.filter(a =>
    !search ||
    a.filename?.toLowerCase().includes(search.toLowerCase()) ||
    a.altText?.toLowerCase().includes(search.toLowerCase()) ||
    a.tags?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "min(900px, 95vw)",
        maxHeight: "85vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, flex: 1 }}>Asset Library</h2>
          <input
            placeholder="Search assets…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "6px 12px", fontSize: 14, width: 200 }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              background: "#6366f1", color: "#fff", border: "none", borderRadius: 8,
              padding: "8px 16px", fontSize: 14, cursor: "pointer", fontWeight: 500
            }}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
          <button
            onClick={() => setShowCapture(s => !s)}
            style={{
              background: "#0f172a", color: "#fff", border: "none", borderRadius: 8,
              padding: "8px 16px", fontSize: 14, cursor: "pointer", fontWeight: 500
            }}
          >
            📸 Capture from Studio
          </button>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280", lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {/* Studio capture form */}
        {showCapture && (
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb", background: "#f8fafc" }}>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
              Describe what you want to capture. Studio will log into staging, navigate, and take a polished screenshot.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                placeholder="e.g. 'The bot workflow builder showing a Transfer step configured for the Sales Team'"
                value={captureDesc}
                onChange={e => setCaptureDesc(e.target.value)}
                style={{ flex: 2, minWidth: 260, border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 14 }}
              />
              <input
                placeholder="URL (optional override)"
                value={captureUrl}
                onChange={e => setCaptureUrl(e.target.value)}
                style={{ flex: 1, minWidth: 160, border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 14 }}
              />
              <button
                onClick={capture}
                disabled={capturing || !captureDesc.trim()}
                style={{
                  background: capturing ? "#94a3b8" : "#10b981", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 20px", fontSize: 14, cursor: capturing ? "not-allowed" : "pointer", fontWeight: 500
                }}
              >
                {capturing ? "Capturing… (30–60s)" : "Capture"}
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: "10px 24px", background: "#fef2f2", color: "#dc2626", fontSize: 13, borderBottom: "1px solid #fecaca" }}>
            {error}
          </div>
        )}

        {/* Grid */}
        <div style={{ overflowY: "auto", padding: 24, flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#9ca3af", padding: 40 }}>Loading assets…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", color: "#9ca3af", padding: 40 }}>
              {search ? "No assets match your search." : "No assets yet. Upload an image or capture from Studio."}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
              {filtered.map(asset => (
                <button
                  key={asset.id}
                  onClick={() => onSelect(asset)}
                  style={{
                    border: "2px solid #e5e7eb", borderRadius: 10, padding: 0, background: "#f9fafb",
                    cursor: "pointer", overflow: "hidden", textAlign: "left",
                    transition: "border-color 0.15s, transform 0.15s",
                    display: "flex", flexDirection: "column"
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#6366f1"; (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#e5e7eb"; (e.currentTarget as HTMLButtonElement).style.transform = "none"; }}
                >
                  <div style={{ height: 130, overflow: "hidden", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={asset.url}
                      alt={asset.altText || asset.filename}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                  </div>
                  <div style={{ padding: "8px 10px" }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#111827", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                      {asset.altText || asset.filename}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, display: "flex", gap: 6, alignItems: "center" }}>
                      {asset.width && asset.height && <span>{asset.width}×{asset.height}</span>}
                      <span style={{
                        background: asset.source === "studio" ? "#ede9fe" : "#f0fdf4",
                        color: asset.source === "studio" ? "#7c3aed" : "#166534",
                        borderRadius: 4, padding: "1px 5px", fontSize: 10, fontWeight: 600
                      }}>
                        {asset.source}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      </div>
    </div>
  );
}
